/**
 * Audit Log API service
 * Handles all audit-related API calls
 */

import {
  AuditLogEntry,
  AuditUser,
  AuditLogFilters,
  GetAuditLogsResponse,
  GetAuditUsersResponse,
  AuditAction,
} from '../types/audit';
import { MONTH_NAMES } from '../types';

const API_URL = process.env.REACT_APP_API_URL || '';

// Token getter function - will be set by AuthContext
// Accepts optional forceRefresh parameter to bypass MSAL cache
let getAccessTokenFn: ((forceRefresh?: boolean) => Promise<string | null>) | null = null;

/**
 * Set the token getter function (called from AuthContext)
 */
export function setAuditTokenGetter(fn: (forceRefresh?: boolean) => Promise<string | null>) {
  getAccessTokenFn = fn;
}

// Track if we're currently refreshing token to avoid multiple refresh attempts
let isRefreshingToken = false;
let tokenRefreshPromise: Promise<string | null> | null = null;

/**
 * Force refresh the token (bypasses MSAL cache and gets new token from Azure AD)
 */
async function forceRefreshToken(): Promise<string | null> {
  if (isRefreshingToken && tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  isRefreshingToken = true;
  tokenRefreshPromise = (async () => {
    try {
      if (getAccessTokenFn) {
        // Pass forceRefresh=true to bypass MSAL's token cache
        return await getAccessTokenFn(true);
      }
      return null;
    } finally {
      isRefreshingToken = false;
      tokenRefreshPromise = null;
    }
  })();

  return tokenRefreshPromise;
}

/**
 * Get authorization headers
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  if (getAccessTokenFn) {
    const token = await getAccessTokenFn();
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
  }
  return {};
}

/**
 * Generic request wrapper with token refresh retry
 */
async function auditRequest<T>(
  url: string,
  options: RequestInit,
  isRetry: boolean = false
): Promise<T> {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers,
    },
  });

  const data = await response.json();

  // Handle 401/403 - token may be expired
  if ((response.status === 401 || response.status === 403) && !isRetry) {
    console.warn('Token may be expired, attempting refresh...');
    const newToken = await forceRefreshToken();
    if (newToken) {
      return auditRequest<T>(url, options, true);
    }
  }

  return data;
}

/**
 * Log an authentication event (login/logout)
 */
export async function logAuthEvent(
  action: 'LOGIN' | 'LOGOUT' | 'ACCESS_DENIED',
  retryDelaysMs: number[] = action === 'LOGOUT' ? [] : [1000, 3000],
): Promise<{ success: boolean; error?: string }> {
  // LOGIN and ACCESS_DENIED also record the agent-key auth snapshot server-side, so they are
  // retried until the server confirms it (the backend returns 500 when that write fails).
  let result: { success: boolean; error?: string } = { success: false };
  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
    if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, retryDelaysMs[attempt - 1]));
    try {
      result = await auditRequest<{ success: boolean; error?: string }>(
        `${API_URL}/audit/auth-event`,
        {
          method: 'POST',
          body: JSON.stringify({ action }),
        }
      );
    } catch (error: any) {
      console.error('Failed to log auth event:', error);
      // Don't throw - auth event logging shouldn't break login/logout flow
      result = { success: false, error: error.message };
    }
    if (result.success) return result;
  }
  return result;
}

/**
 * Get audit logs with filters and pagination
 */
export async function getAuditLogs(
  filters: AuditLogFilters,
  nextToken?: string
): Promise<GetAuditLogsResponse> {
  try {
    // Build query string. Dates are optional when querying one resource's
    // full history (resourceId).
    const params = new URLSearchParams();
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.userId) {
      params.append('userId', filters.userId);
    }
    if (filters.action) {
      params.append('action', filters.action);
    }
    if (filters.resourceId) {
      params.append('resourceId', filters.resourceId);
    }
    if (nextToken) {
      params.append('nextToken', nextToken);
    }

    return await auditRequest<GetAuditLogsResponse>(
      `${API_URL}/audit/logs?${params.toString()}`,
      { method: 'GET' }
    );
  } catch (error: any) {
    console.error('Failed to get audit logs:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get distinct users from audit logs (for filter dropdown)
 */
export async function getAuditUsers(): Promise<GetAuditUsersResponse> {
  try {
    return await auditRequest<GetAuditUsersResponse>(
      `${API_URL}/audit/users`,
      { method: 'GET' }
    );
  } catch (error: any) {
    console.error('Failed to get audit users:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Format audit action for display
 */
export function formatAuditAction(action: AuditAction): string {
  switch (action) {
    case 'LOGIN':
      return 'Login';
    case 'LOGOUT':
      return 'Logout';
    case 'ACCESS_DENIED':
      return 'Access Denied';
    case 'PROJECTION_EDIT':
      return 'Projection Edit';
    case 'USER_ASSIGNMENT_UPDATE':
      return 'User Assignment Update';
    case 'ARCHIVE_PROJECTION_UNLOCK':
      return 'Archive Projection Unlock';
    case 'PROJECTION_MONTHLY_ADJUSTMENT':
      return 'Projection Monthly Adjustment';
    case 'NUT_SHEET_CREATE':
      return 'Nut Sheet — Row Created';
    case 'NUT_SHEET_UPDATE':
      return 'Nut Sheet — Row Updated';
    case 'NUT_SHEET_DELETE':
      return 'Nut Sheet — Row Deleted';
    case 'AGENT_KEY_CREATE':
      return 'Agent Access — Setup Code Created';
    case 'AGENT_KEY_REDEEM':
      return 'Agent Access — Agent Connected';
    case 'AGENT_KEY_REVOKE':
      return 'Agent Access — Agent Disconnected';
    default:
      return action;
  }
}

/**
 * Human labels for Nut Sheet entry fields (used by the row-history viewer and
 * the global audit viewer when rendering NUT_SHEET_* changes).
 */
export const NUT_SHEET_FIELD_LABELS: Record<string, string> = {
  type: 'Type',
  category: 'Category',
  stockNumber: 'Stock #',
  dealNumber: 'Deal #',
  soldYear: 'Sold Year',
  soldMake: 'Sold Make',
  soldModel: 'Sold Model',
  tradeStockNumber: 'Trade Stock #',
  tradeYear: 'Trade Year',
  tradeMake: 'Trade Make',
  tradeModel: 'Trade Model',
  tradeMiles: 'Trade Miles',
  tradeComment: 'Trade Comment',
  trade2StockNumber: 'Trade 2 Stock #',
  trade2Year: 'Trade 2 Year',
  trade2Make: 'Trade 2 Make',
  trade2Model: 'Trade 2 Model',
  trade2Miles: 'Trade 2 Miles',
  trade2Comment: 'Trade 2 Comment',
  acv: 'T1 ACV',
  retail: 'T1 Retail',
  t1Plan: 'T1 Plan',
  trade2Acv: 'T2 ACV',
  trade2Retail: 'T2 Retail',
  t2Plan: 'T2 Plan',
  daysInStock: 'Days in Stock',
  estimatedGross: 'Est. Gross',
  actualGross: 'Actual Gross',
  fAndI: 'F&I',
  points: 'Back End $',
  holdback: 'Holdback',
  doc: 'Doc',
  financeSource: 'Finance Source',
  openROs: 'Open ROs',
  salesperson: 'Salesperson',
  salesperson2: 'Salesperson 2',
  salesManager: 'Sales Manager',
  fiRep: 'F&I Rep',
  customerLastName: 'Customer Last Name',
  accountingComment: 'Accounting Comment',
  comment: 'Deal Comment',
};

/** Nut Sheet fields rendered as currency. */
export const NUT_SHEET_MONEY_FIELDS = new Set<string>([
  'acv', 'retail', 'trade2Acv', 'trade2Retail',
  'estimatedGross', 'actualGross', 'fAndI', 'points', 'holdback', 'doc',
]);

/**
 * Format field name for display (e.g., "NEW_VEHICLE#GROSS" -> "New Vehicle - Gross")
 */
export function formatFieldName(field: string): string {
  // "DEPT#METRIC" for annual projection edits; "DEPT#METRIC#monthN" for executive monthly adjustments
  const [department, metric, monthKey] = field.split('#');

  const deptNames: Record<string, string> = {
    'NEW_VEHICLE': 'New Vehicle',
    'USED_VEHICLE': 'Used Vehicle',
    'SERVICE': 'Service',
    'PARTS': 'Parts',
    'BODY_SHOP': 'Body Shop',
    'RENTAL': 'Rental',
    'OTHERS': 'Others',
  };

  const metricNames: Record<string, string> = {
    'GROSS': 'Gross',
    'SALES': 'Sales',
    'UNITS': 'Units',
    'FI_INCOME': 'F&I Income',
    'RENTAL_INCOME': 'Rental Income',
    'VARIABLE_EXPENSES': 'Variable Expenses',
    'SEMI_FIXED_EXPENSES': 'Semi-Fixed Expenses',
    'FIXED_EXPENSES': 'Fixed Expenses',
    'OTHER_EXPENSES': 'Other Expenses',
  };

  // Nut Sheet fields are flat keys (no "#") — fall back to their label map.
  if (!field.includes('#') && NUT_SHEET_FIELD_LABELS[field]) {
    return NUT_SHEET_FIELD_LABELS[field];
  }

  const deptName = deptNames[department] || department;
  const metricName = metricNames[metric] || metric;
  const month = monthKey && /^month(\d{1,2})$/.test(monthKey)
    ? MONTH_NAMES[Number(monthKey.replace('month', '')) - 1]
    : undefined;

  return month ? `${deptName} - ${metricName} (${month})` : `${deptName} - ${metricName}`;
}

/**
 * Format an audit value for display. Numbers render as whole-dollar currency
 * (projection + Nut Sheet money fields); strings pass through; null shows a dash.
 */
export function formatCurrencyValue(value: string | number | boolean | null): string {
  if (value === null || value === undefined) return '(new)';
  if (typeof value === 'number') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }
  return String(value);
}

/**
 * Format a projection change value by its field: unit counts ("NEW_VEHICLE#UNITS",
 * "NEW_VEHICLE#UNITS_Ford", "...#UNITS#month3") as plain numbers, everything else as currency.
 */
export function formatProjectionChangeValue(field: string, value: string | number | boolean | null): string {
  const metric = field.split('#')[1] || '';
  if (typeof value === 'number' && (metric === 'UNITS' || metric.startsWith('UNITS_'))) {
    return value.toLocaleString('en-US');
  }
  return formatCurrencyValue(value);
}

/**
 * Format a Nut Sheet field value: currency for money fields, plain text
 * otherwise; null/empty shows a dash.
 */
export function formatNutSheetValue(
  field: string,
  value: string | number | boolean | null,
): string {
  if (value === null || value === undefined || value === '') return '—';
  if (NUT_SHEET_MONEY_FIELDS.has(field) && typeof value === 'number') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }
  return String(value);
}
